import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { LOGO_URL } from "@/lib/api";

const links = [
  { to: "/", label: "Home", end: true },
  { to: "/#services", label: "Services" },
  { to: "/meet-greet", label: "Meet & Greet" },
  { to: "/reviews", label: "Reviews" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname, hash } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      setOpen(false);
      if (hash) {
        const el = document.querySelector(hash);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }, 0);
    return () => clearTimeout(t);
  }, [pathname, hash]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#F4E8C8]/90 backdrop-blur-md border-b border-[#3F2A56]/15"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-3 group">
          <img
            src={LOGO_URL}
            alt="Happy Tails by Aurora"
            className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover border-2 border-[#3F2A56]/15 shadow-sm group-hover:rotate-3 transition-transform"
          />
          <div className="leading-tight hidden sm:block">
            <div className="font-display text-[1.45rem] font-bold text-[#3F2A56]">
              Happy Tails
            </div>
            <div className="font-script text-[#b85b7e] -mt-1 text-lg">by Aurora</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <NavItem key={l.to} {...l} />
          ))}
          <Link
            to="/book"
            data-testid="nav-book-cta"
            className="ml-3 rounded-full bg-[#3F2A56] hover:bg-[#523870] text-[#F4E8C8] font-semibold px-5 py-2.5 transition-all hover:-translate-y-0.5 shadow-sm"
          >
            Book Now
          </Link>
        </nav>

        <button
          data-testid="nav-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden p-2 rounded-full text-[#3F2A56] hover:bg-[#3F2A56]/10"
          aria-label="Toggle menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-[#FFFBF0] border-t border-[#3F2A56]/10 px-5 py-4 space-y-1">
          {links.map((l) => (
            <NavItem key={l.to} {...l} mobile />
          ))}
          <Link
            to="/book"
            data-testid="nav-book-cta-mobile"
            className="block mt-3 text-center rounded-full bg-[#3F2A56] text-[#F4E8C8] font-semibold px-5 py-3"
          >
            Book Now
          </Link>
        </div>
      )}
    </header>
  );
}

function NavItem({ to, label, end, mobile }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={`nav-${label.toLowerCase()}`}
      className={({ isActive }) =>
        `${mobile ? "block py-2.5" : "px-4 py-2"} rounded-full font-semibold text-[15px] transition-colors ${
          isActive
            ? "text-[#3F2A56]"
            : "text-[#5C4A73] hover:text-[#3F2A56] hover:bg-[#3F2A56]/5"
        }`
      }
    >
      {label}
    </NavLink>
  );
}
